<template>
  <div class="container-fluid px-4">
    <div class="row justify-content-center">
      <div class="col-md-6">
        <div class="card mt-5">
          <div class="card-body p-5">
            <!-- Loading state -->
            <div v-if="loading" class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
              <p class="mt-3">{{ $t('email.complete.processing') }}</p>
            </div>

            <!-- Success state -->
            <div v-else-if="success">
              <div class="text-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" class="bi bi-check-circle-fill text-success" viewBox="0 0 16 16">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                </svg>
              </div>

              <h2 class="h3 text-center mb-3">{{ $t('email.complete.title') }}</h2>
              <p class="text-center text-muted mb-4">
                {{ $t('email.complete.message') }}
              </p>

              <button
                type="button"
                class="btn btn-primary w-100"
                @click="goToLogin"
              >
                {{ $t('email.complete.loginButton') }}
              </button>
            </div>

            <!-- Error state -->
            <div v-else-if="error">
              <div class="text-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" class="bi bi-x-circle-fill text-danger" viewBox="0 0 16 16">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                </svg>
              </div>

              <h2 class="h3 text-center mb-3">{{ $t('email.complete.errorTitle') }}</h2>
              <p class="text-center text-danger mb-4">
                {{ errorMessage }}
              </p>

              <button
                type="button"
                class="btn btn-outline-primary w-100"
                @click="goToLogin"
              >
                {{ $t('email.complete.loginButton') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { confirmEmailChange } from '../../api/user/AuthApi.js';
import { colors } from '../../../consts/colors.js';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const loading = ref(true);
const success = ref(false);
const error = ref(false);
const errorMessage = ref('');

/**
 * Confirm email change on component mount
 */
onMounted(async () => {
  const token = route.params.token;

  if (!token) {
    loading.value = false;
    error.value = true;
    errorMessage.value = t('email.complete.invalidToken');
    return;
  }

  try {
    await confirmEmailChange(token);
    loading.value = false;
    success.value = true;
  } catch (err) {
    loading.value = false;
    error.value = true;

    if (err.status === 404) {
      errorMessage.value = t('email.complete.tokenNotFound');
    } else if (err.status === 422) {
      errorMessage.value = err.message || t('email.complete.tokenExpired');
    } else {
      errorMessage.value = t('email.complete.failed');
    }
  }
});

/**
 * Navigate to login page
 */
const goToLogin = () => {
  router.push({ name: 'login' });
};
</script>

<style lang="scss" scoped>
.card {
  border: 1px solid v-bind('colors.borderMediumGray');
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-body {
  background-color: v-bind('colors.white');
}

.btn {
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background-color: v-bind('colors.btnBgColorPrimary');
  border-color: v-bind('colors.btnBgColorPrimary');
  color: v-bind('colors.white');

  &:hover {
    background-color: v-bind('colors.blue[600]');
  }

  &:active {
    background-color: v-bind('colors.blue[700]');
  }
}

.btn-outline-primary {
  background-color: v-bind('colors.white');
  color: v-bind('colors.btnBgColorPrimary');
  border: 1px solid v-bind('colors.btnBgColorPrimary');

  &:hover {
    background-color: v-bind('colors.gray[50]');
  }

  &:active {
    background-color: v-bind('colors.gray[100]');
  }
}

.text-success {
  color: #28a745;
}

.text-danger {
  color: v-bind('colors.error');
}

.text-muted {
  color: v-bind('colors.textSecondary');
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>
